import * as Sentry from "@sentry/react";
import { toPng } from "html-to-image";
import { useRef } from "react";

import { useAppSelector } from "~/app/store";

import {
  selectUsersClientId,
  selectUsersProfileName
} from "~/redux/selectors/authSelectors";
import { selectSiteFulfillmentCenterId } from "~/redux/selectors/siteSelectors";
import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";
import { useSendSupportBundleMutation } from "~/redux/warehouse/andon.hooks";

import { selectNetworkLogs, SupportBundle } from "./supportBundle.slice";

export const useSupportBundle = () => {
  const lastSentRef = useRef(0);

  const networkLogs = useAppSelector(selectNetworkLogs);
  const profileName = useAppSelector(selectUsersProfileName);
  const siteFulfillmentCenterId = useAppSelector(selectSiteFulfillmentCenterId);
  const clientId = useAppSelector(selectUsersClientId);
  const workstation = useAppSelector(selectThisWorkstation);

  const [sendBundle, { isLoading }] = useSendSupportBundleMutation();

  const getSentryData = (): SupportBundle["sentry"] => {
    const sentryScope = Sentry.getCurrentScope();
    const session = sentryScope.getSession();
    return {
      sessionId: session?.sid,
      timestamp: session?.timestamp,
      started: session?.started,
      duration: session?.duration,
      status: session?.status,
      release: session?.release,
      environment: session?.environment,
      replayId: Sentry.lastEventId(),
      traceId: sentryScope.getPropagationContext().traceId
    };
  };

  const createBundle = async (): Promise<SupportBundle> => {
    return {
      timestamp: new Date().toString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      user: {
        profileName,
        siteFulfillmentCenterId,
        clientId,
        workstation
      },
      sentry: getSentryData(),
      screenshot: await toPng(document.body),
      networkLogs
    };
  };

  const sendSupportBundle = async () => {
    const now = Date.now();
    const timeSinceLastSent = now - lastSentRef.current;
    if (timeSinceLastSent < 10_000) {
      return;
    }
    lastSentRef.current = now;

    try {
      const bundle = await createBundle();
      await sendBundle(bundle).unwrap();
      Sentry.captureMessage("Support bundle submitted", {
        level: "info",
        tags: {
          profileName,
          timestamp: bundle.timestamp
        }
      });
    } catch (error) {
      // Don't show an error toast as we silently send the bundle.
      console.error("Faild to send support bundle", error);
    }
  };

  return {
    sendSupportBundle,
    isSendingSupportBundle: isLoading
  };
};
